import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import type { ObjectiveTypeAccess } from "@/modules/performance/shared/constants";

import {
  type AdminObjective,
  type AdminObjectiveActivityComment,
  type AdminObjectivePeriod,
} from "../types";

export type ObjectiveActivities = Paginate<AdminObjectiveActivityComment<true>>;
export type ObjectiveList = Paginate<
  AdminObjective<true, false, true, false, true, true, false>
>;

export type ObjectivePeriodList = Paginate<AdminObjectivePeriod<true>>;

export const objectivePeriods = createQueryKeys(
  "admin-performance-objective-periods",
  {
    all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    users: (userId: number | null, page?: number) => [{ userId, page }],
    one: (id?: number) => ({
      queryKey: [id],
      contextQueries: {
        summary: null,
        tags_summary: null,
        activities: null,
        objectives: null,
      },
    }),
  },
);

const include = "user,objectiveType,tags,keyResults";

export const fetchObjectivePeriodsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...objectivePeriods.all(page, search),
    queryFn: () =>
      adminApi
        .get<ObjectivePeriodList>(`/performance/objective-periods`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchObjectivePeriods = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchObjectivePeriodsQueryOptions(page, search));

export const fetchObjectivePeriodQueryOptions = (id?: number) =>
  queryOptions({
    ...objectivePeriods.one(id),
    queryFn: () =>
      adminApi
        .get<AdminObjectivePeriod<true>>(
          `/performance/objective-periods/${id}`,
          {
            params: { include },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchObjectivePeriod = (id?: number) =>
  useQuery(fetchObjectivePeriodQueryOptions(id));

export interface ObjectivePeriodRequest {
  name: string;
  is_company_wide: boolean;
  unit_ids: number[];
  start_date: string;
  end_date: string;
  initial_objective_due_date: string;
  has_weight: boolean;
  has_type_weight: boolean;
  performance_objective_types: (Partial<ObjectiveTypeAccess> & {
    id: number;
    min_objectives: number | null;
    max_objectives: number | null;
    weight: string | null;
    is_key_result_mandatory?: boolean;
  })[];
  participants?: number[];
  has_checkin: boolean;
  need_participant_checkin_approval: boolean;
  limit_checkin_interval: number | null;
  performance_objective_period_checkin_form_id: number | null;
}

export const useCreateObjectivePeriod = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: ObjectivePeriodRequest) =>
      adminApi
        .post("/performance/objective-periods", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectivePeriods.all._def,
      });
    },
  });
};

export interface UpdateObjectivePeriodRequest {
  name: string;
  can_create_objectives: boolean;
  can_create_team_objectives: boolean;
  can_view_manager_objectives: boolean;
  need_manager_approval: boolean;
  participants?: number[];
}

export const useUpdateObjectivePeriod = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UpdateObjectivePeriodRequest) =>
      adminApi
        .put(`/performance/objective-periods/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectivePeriods.all._def,
      });
      queryClient.invalidateQueries(objectivePeriods.one(id));
    },
  });
};

export interface UpdateObjectivePeriodDatesRequest {
  start_date: string;
  end_date: string;
  initial_objective_due_date: string;
  manager_approval_days?: number | null;
}

export const useUpdateObjectivePeriodDates = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: UpdateObjectivePeriodDatesRequest) =>
      adminApi
        .put(`/performance/objective-periods/${id}/dates`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: objectivePeriods.all._def,
      });
      queryClient.invalidateQueries(objectivePeriods.one(id));
    },
  });
};
